import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

const Stat = ({ label, value, accent, testId }) => (
  <Card className="bg-wt-surface border-wt-border">
    <CardContent className="p-3">
      <p className="text-[11px] text-wt-text-muted">{label}</p>
      <p className={`text-xl font-bold ${accent || 'text-wt-text-primary'}`} data-testid={testId}>{value}</p>
    </CardContent>
  </Card>
);

export default function MySalesDashboardPage() {
  const [month, setMonth] = useState(thisMonth());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sales/performance/me?month=${month}`, auth());
      setD(r.data);
    } catch (e) { /* handled by empty state */ }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-wt-gold" /></div>;
  if (!d) return <div className="p-8 text-center text-wt-text-muted">Could not load your sales dashboard</div>;

  const progress = Math.min(d.achievement_pct || 0, 100);

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="my-sales-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-wt-text-primary flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-wt-gold" /> My Sales
          </h1>
          <p className="text-sm text-wt-text-muted">Your targets, delivered sales & incentives</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="w-44 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="my-sales-month-picker" />
      </div>

      {!d.incentive_enabled && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/40 flex items-start gap-2" data-testid="incentives-disabled-banner">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-300">Incentives are disabled for your profile. Please contact management.</p>
            <p className="text-xs text-red-200/70 mt-0.5">Your sales are still tracked below, but no incentive is calculated.</p>
          </div>
        </div>
      )}
      {d.incentive_enabled && !d.eligible && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-300" data-testid="eligibility-warning-banner">
          {d.eligibility_reason}
        </div>
      )}

      <Card className="bg-wt-surface border-wt-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <p className="text-sm text-wt-text-muted">Monthly Target Progress</p>
            <div className="flex items-center gap-2">
              {(d.eligible_incentive_types || []).map(t => (
                <Badge key={t} className="bg-wt-gold/15 text-wt-gold border border-wt-gold/40 text-[10px] capitalize">{t.replace(/_/g, ' ')}</Badge>
              ))}
              {d.payout_status && <Badge className="bg-zinc-500/20 text-zinc-300 border border-zinc-500/40 text-[10px] capitalize">Payout: {d.payout_status}</Badge>}
            </div>
          </div>
          <div className="flex items-end justify-between mb-2">
            <p className="text-2xl font-bold text-wt-gold" data-testid="my-achieved-value">{fmt(d.achieved)}</p>
            <p className="text-sm text-wt-text-muted">of {fmt(d.target)} ({d.achievement_pct}%)</p>
          </div>
          <div className="h-2.5 bg-wt-bg rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${progress >= 100 ? 'bg-emerald-500' : progress >= 70 ? 'bg-wt-gold' : 'bg-amber-500'}`} style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-wt-text-muted mt-2">
            Remaining: {fmt(d.remaining)} · Minimum eligibility: {fmt(d.min_eligibility)} · Current slab: <span className="text-wt-gold">{d.slab_pct}%</span>
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Estimated Incentive" value={fmt(d.estimated_incentive)} accent="text-emerald-400" testId="my-estimated-incentive" />
        <Stat label="Delivered Orders" value={d.delivered_orders} />
        <Stat label="Pending Orders" value={d.pending_orders} />
        <Stat label="Cancelled / Returned" value={`${d.cancelled_orders} / ${d.returned_orders}`} />
        <Stat label="Leads Assigned" value={d.leads_assigned} />
        <Stat label="Leads Converted" value={d.leads_converted} />
        <Stat label="Conversion Rate" value={d.conversion_rate != null ? `${d.conversion_rate}%` : '—'} />
        <Stat label="Follow-ups Pending" value={d.followups_pending} accent={d.followups_pending > 0 ? 'text-amber-400' : undefined} />
        <Stat label="Stale Leads (3d+)" value={d.stale_leads} accent={d.stale_leads > 0 ? 'text-red-400' : undefined} />
        <Stat label="WhatsApp Chats Handled" value={d.conversations_handled} />
      </div>
    </div>
  );
}
